import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import { formatDateTime } from '@/lib/utils';
import { ModuleTag, type RecordItem } from '@/types';

interface ModuleViewProps {
  prisonerId: string;
}

const moduleLabels: Record<ModuleTag, string> = {
  [ModuleTag.VOLLZUGSVERLAUF]: 'Vollzugsverlauf',
  [ModuleTag.VOLLZUGSPLANUNG]: 'Vollzugsplanung',
  [ModuleTag.DISZIPLINAR]: 'Disziplinarmaßnahmen',
  [ModuleTag.LOCKERUNGEN]: 'Lockerungen/Ausgänge',
  [ModuleTag.BESUCHE]: 'Besuche/Kontakte',
  [ModuleTag.DOKUMENTE]: 'Dokumente',
  [ModuleTag.NOTIZEN]: 'Notizen/Vermerke',
  [ModuleTag.VOLLSTRECKUNG]: 'Vollstreckung',
};

export function ModuleView({ prisonerId }: ModuleViewProps) {
  const [selectedModule, setSelectedModule] = useState<ModuleTag | null>(null);

  const { data: records, isLoading } = useQuery({
    queryKey: ['records', prisonerId, selectedModule],
    queryFn: () =>
      api.getRecords(prisonerId, {
        module: selectedModule || undefined,
      }),
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[250px,1fr]">
      {/* Module Sidebar */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Module</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <Button
            variant={selectedModule === null ? 'secondary' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setSelectedModule(null)}
          >
            Alle Module
          </Button>
          {Object.values(ModuleTag).map((module) => (
            <Button
              key={module}
              variant={selectedModule === module ? 'secondary' : 'ghost'}
              className="w-full justify-start"
              onClick={() => setSelectedModule(module)}
            >
              {moduleLabels[module]}
            </Button>
          ))}
        </CardContent>
      </Card>

      {/* Content */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedModule ? moduleLabels[selectedModule] : 'Alle Einträge'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Lädt...</p>
          ) : !records?.length ? (
            <p className="text-muted-foreground">Keine Einträge vorhanden.</p>
          ) : (
            <div className="space-y-4">
              {records.map((record: RecordItem) => (
                <div
                  key={record.id}
                  className="rounded-lg border p-4 hover:bg-muted/50"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{record.title}</h4>
                      <p className="text-sm text-muted-foreground">
                        {record.type} • {formatDateTime(record.createdAt)}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {record.modules.map((m) => (
                        <span
                          key={m}
                          className="rounded bg-muted px-2 py-0.5 text-xs"
                        >
                          {moduleLabels[m]}
                        </span>
                      ))}
                    </div>
                  </div>
                  {record.content && (
                    <p className="mt-2 text-sm">{record.content}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
